import React, { useEffect } from "react";
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import am4themes_animated from "@amcharts/amcharts4/themes/animated";
import bulletSVG from "../../assets/img/bullet.svg";
import { truncateWithEllipsis } from "@amcharts/amcharts4/.internal/core/utils/Utils";

interface ChartInterface {
  data: any;
  seriesData: any;
  chartId: any;
}

const Chart: React.FC<ChartInterface> = ({ data, seriesData, chartId }) => {
  useEffect(() => {
    am4core.options.commercialLicense = true;
    am4core.useTheme(am4themes_animated);

    let chart = am4core.create(chartId, am4charts.XYChart);
    chart.data = data?.records;

    const xAxis = chart.xAxes.push(new am4charts.DateAxis());
    xAxis.renderer.labels.template.disabled = data.xAxesLabelVisible;
    xAxis.renderer.labels.template.fill = am4core.color(data.xAxesColor);

    xAxis.dateFormatter = new am4core.DateFormatter();
    xAxis.dateFormatter.dateFormat = "MMM dd, yyyy";
    chart.dateFormatter.inputDateFormat = "MMM/dd/yyyy";

    const yAxis = chart.yAxes.push(new am4charts.ValueAxis());
    yAxis.renderer.labels.template.disabled = data.yAxesLabelVisible;
    yAxis.renderer.labels.template.fill = am4core.color(data.yAxesColor);
    yAxis.renderer.labels.template.fontSize = "14";
    const seriesArray = [];
    for (let i = 0; i < seriesData.length; i++) {
      const series = chart.series.push(new am4charts.LineSeries());
      series.dataFields.dateX = seriesData[i].valueX;
      series.dataFields.valueY = seriesData[i].valueY;
      series.fillOpacity = 0.01;
      series.strokeWidth = 4;
      series.stroke = am4core.color(seriesData[i].color);

      series.fill = am4core.color(seriesData[i].color);

      series.tooltipHTML = ` <div 
      data-model-id="1080:27089"
      style="
        position: relative;
        width: 210px;
        height: 79px;
      "
    >
      <div style="
        display: flex;
        width: 210px;
        height: 34px;
        align-items: center;
        gap: 12px;
        padding: 8px;
        position: absolute;
        top: 0;
        left: 0;
        border-bottom: 1px solid rgba(74, 84, 99, 1);
      ">
        <div style="
          position: relative;
          width: 105px;
          margin-top: -1px;
          font-family: 'Mulish', Helvetica;
          font-weight: 400;
          color: rgba(247, 249, 249, 1);
          font-size: 12px;
          letter-spacing: 0px;
          line-height: 18px;
          font-style: normal;
        ">{dateX.formatDate('MMM/dd/yyyy, HH:mm')}</div>
      </div>
      <div style="
        display: flex;
        flex-direction: column;
        width: 210px;
        align-items: flex-start;
        gap: 10px;
        position: absolute;
        top: 38px;
        left: 0;
      ">
        <div style="
          position: relative;
          align-self: stretch;
          width: 100%;
          height: 41px;
        ">
          <div style="
            display: flex;
            width: 209px;
            align-items: center;
            justify-content: space-between;
            position: relative;
            top: 6px;
            left: 1px;
          ">
            <div style="
              display: inline-flex;
              align-items: center;
              gap: 4px;
              padding: 0px 0px 0px 8px;
              position: relative;
              flex: 0 0 auto;
            ">
              <div style="
                position: relative;
                width: 2px;
                height: 24px;
                background-color: rgba(0, 156, 161, 1);
                border-radius: 4px;
              "></div>
              <div style="
                position: relative;
                width: 106px;
                height: 20px;
                margin-right: -2px;
              ">
                <div style="
                  position: absolute;
                  height: 20px;
                  top: 0;
                  left: 0;
                  font-family: 'Mulish', Helvetica;
                  font-weight: 700;
                  color: rgba(247, 249, 249, 1);
                  font-size: 14px;
                  letter-spacing: 0px;
                  line-height: 20px;
                  white-space: nowrap;
                  font-style: normal;
                ">Value</div>
              </div>
            </div>
            <div style="
              display: inline-flex;
              align-items: center;
              gap: 7px;
              padding: 0px 8px 0px 0px;
              position: relative;
              flex: 0 0 auto;
            ">
              <div style="
                position: relative;
                width: fit-content;
                margin-top: -1px;
                font-family: 'Mulish', Helvetica;
                font-weight: 400;
                color: #F7F9F9 !important; 
                font-size: 14px;
                text-align: right;
                letter-spacing: 0px;
                line-height: 20px;
                white-space: nowrap;
                font-style: normal;
              ">{valueY}</div>
            </div>
          </div>
        </div>
      </div>
    </div>`;
      if (series.tooltip) {
        series.tooltip.getFillFromObject = false;
        series.tooltip.pointerOrientation = "vertical";
        series.tooltip.background.fill = am4core.color("#001023");
        series.tooltip.background.stroke = am4core.color("#4A5463");
        series.tooltip.background.strokeWidth = 1;
        series.tooltip.background.cornerRadius = 4;
        series.tooltip.background.fillOpacity = 1;
        series.tooltip.background.strokeOpacity = 1;
        series.tooltip.zIndex = 0;
      }

      if (seriesData[i]?.strokeDasharray === "Dotted")
        series.strokeDasharray = "10,10";
      seriesArray.push(series);
    }
    seriesArray.forEach((series) => {
      series.appear();
    });

    chart.cursor = new am4charts.XYCursor();
    chart.cursor.snapToSeries = seriesArray;
    chart.exporting.menu = new am4core.ExportMenu();
    chart.cursor.lineX.disabled = true;
    chart.cursor.lineY.disabled = true;
    var lineX = chart.plotContainer.createChild(am4core.Line);
    var lineY = chart.plotContainer.createChild(am4core.Line);

    chart.cursor.zIndex = 1;

    let hoverBullet = chart.plotContainer.createChild(am4core.Image);
    hoverBullet.zIndex = 0;
    hoverBullet.href = bulletSVG;
    hoverBullet.width = 24;
    hoverBullet.height = 24;
    hoverBullet.horizontalCenter = "middle";
    hoverBullet.verticalCenter = "middle";
    hoverBullet.opacity = 0;

    hoverBullet.adapter.add("tooltipPosition", function (value, target, key) {
      if (target.dataItem) {
        hoverBullet.opacity = 1;
      } else {
        hoverBullet.opacity = 0;
      }
      return value;
    });

    chart.cursor.events.on("cursorpositionchanged", function (ev) {
      if (ev.target.snapToSeries) {
        let point = ev.target?.point;
        const isAnySeriesHovered = Array.isArray(ev.target.snapToSeries)
          ? ev.target.snapToSeries.some((series: any) => series.tooltip.visible)
          : false;

        if (point && isAnySeriesHovered) {
          hoverBullet.dx = ev.target.point.x;
          hoverBullet.dy = ev.target.point.y;

          hoverBullet.opacity = 1;
        } else {
          hoverBullet.opacity = 0;
        }
      } else {
        hoverBullet.dx = -1000;
        hoverBullet.dy = -1000;
        hoverBullet.opacity = 0;
      }
    });
    chart.cursor.events.on("cursorpositionchanged", function (ev) {
      var cursor = ev.target;
      let currentStroke = am4core.color("#cccccc");

      chart.series.each(function (series) {
        if (series.tooltipDataItem) {
          currentStroke = series.stroke as am4core.Color;
        }
      });

      lineX.stroke = currentStroke;
      lineY.stroke = currentStroke;
      lineY.strokeDasharray = "3,3";
      lineX.strokeDasharray = "3,3";
      lineX.opacity=0.7;
      lineY.opacity=0.7;

      if (cursor && cursor.point) {
        lineX.disabled = false;
        lineY.disabled = false;
        lineX.x1 = lineX.x2 = cursor.point.x;
        lineX.y1 = 0;
        lineX.y2 = chart.plotContainer.pixelHeight;

        lineY.y1 = lineY.y2 = cursor.point.y;
        lineY.x1 = 0;
        lineY.x2 = chart.plotContainer.pixelWidth;

        lineX.visible = true;
        lineY.visible = true;
      } else {
        lineX.visible = false;
        lineY.visible = false;
        lineX.disabled = true;
        lineY.disabled = true;
        lineX.hidden = true;
        lineY.hidden = true;
      }
    });

    chart?.events.on("appeared", function (e) {
      hoverBullet.opacity = 0;
    });

    return () => {
      chart.dispose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, seriesData, chartId]);

  return <div id={chartId} style={{ width: "1000px", height: "500px" }} />;
};

export default Chart;
